import { useEffect, useRef, useState } from 'react';
import { ELLIPSIS, getPageItems } from './pageList';

// SOW 4 A1d, frame `pipeline_2` (layers `pagination`, `Group 963`); values in
// files/tasks/sow4-a1-pipeline/spec.md section d. `page` is 0-based, as MUI's paginationModel.

const Chevron = ({ direction }) => (
  <svg width="5" height="8" viewBox="0 0 5 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d={direction === 'prev' ? 'M3.5 1L0.5 4L3.5 7' : 'M1.5 7L4.5 4L1.5 1'}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SizeTick = () => (
  <svg width="9" height="7" viewBox="0 0 9 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M0.5 2.89L3.3 5.95L8.5 0.5" stroke="#4CBC18" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const NAV_BUTTON = 'flex h-[24px] w-[24px] items-center justify-center rounded-full';

const PageSizeSelect = ({ pageSize, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const toggleRef = useRef(null);
  const listRef = useRef(null);

  const close = (returnFocus) => {
    setOpen(false);
    if (returnFocus) toggleRef.current?.focus();
  };

  // Keyboard: focus lands on the current size when the menu opens; buttons give Enter/Space.
  useEffect(() => {
    if (!open) return;
    const buttons = [...(listRef.current?.querySelectorAll('button') ?? [])];
    (buttons.find((b) => b.getAttribute('aria-selected') === 'true') ?? buttons[0])?.focus();
  }, [open]);

  const onListKeyDown = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const buttons = [...listRef.current.querySelectorAll('button')];
    const i = buttons.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? Math.min(i + 1, buttons.length - 1) : Math.max(i - 1, 0);
    buttons[next]?.focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close(true);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const tone = open ? 'border-brand-hover text-brand-hover' : 'border-brand-fieldBdr text-brand-dark';

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={toggleRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Rows per page"
        onClick={() => setOpen((v) => !v)}
        className={`flex h-[26px] w-[92px] items-center justify-between rounded-[13px] border bg-brand-greenSec pl-[10px] pr-[10px] text-[13px] font-medium hover:border-brand-hover hover:text-brand-hover ${tone}`}
      >
        <span>{pageSize} / page</span>
        <svg width="7" height="4" viewBox="0 0 7 4" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M0.5 0.5L3.5 3.5L6.5 0.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label="Rows per page"
          onKeyDown={onListKeyDown}
          className="absolute left-1/2 top-[31px] z-10 w-[80px] -translate-x-1/2 rounded-[8px] border border-[#C8D2C3] bg-white py-[7px] shadow-[0_0_40px_rgba(115,147,94,0.15)]"
        >
          {options.map((size) => {
            const selected = size === pageSize;
            return (
              <li key={size} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    close(true);
                    if (!selected) onChange(size);
                  }}
                  className="relative flex h-[26px] w-full cursor-pointer items-center pl-[23px] text-left text-[13px] font-normal text-brand-dark outline-none hover:bg-[#EEF3EC] focus-visible:bg-[#EEF3EC]"
                >
                  {size}
                  {selected && (
                    <span className="absolute left-[55px] top-1/2 -translate-y-1/2 flex">
                      <SizeTick />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const PipelinePagination = ({ page, pageSize, rowCount, pageSizeOptions, onPageChange, onPageSizeChange }) => {
  const pageCount = Math.max(1, Math.ceil(rowCount / pageSize));
  const current = Math.min(page, pageCount - 1) + 1;
  const items = getPageItems(current, pageCount);
  const atFirst = current === 1;
  const atLast = current === pageCount;

  return (
    <nav
      aria-label="Pagination"
      className="flex flex-wrap items-center justify-center gap-x-[20px] gap-y-[12px]"
    >
      <div className="flex items-center gap-x-[6px]">
        <button
          type="button"
          aria-label="Previous page"
          disabled={atFirst}
          onClick={() => onPageChange(current - 2)}
          className={`${NAV_BUTTON} mr-[2px] ${atFirst ? 'text-brand-disabled cursor-default' : 'text-brand-dark hover:text-brand-hover'}`}
        >
          <Chevron direction="prev" />
        </button>
        {items.map((item, i) =>
          item === ELLIPSIS ? (
            <span
              key={`gap-${i}`}
              aria-hidden="true"
              className="flex h-[24px] min-w-[24px] items-center justify-center text-[13px] font-medium text-brand-dark"
            >
              {ELLIPSIS}
            </span>
          ) : (
            <button
              key={item}
              type="button"
              aria-label={`Page ${item}`}
              aria-current={item === current ? 'page' : undefined}
              onClick={() => item !== current && onPageChange(item - 1)}
              className={`flex h-[24px] min-w-[24px] items-center justify-center rounded-full px-[2px] text-[13px] font-medium ${
                item === current ? 'bg-brand-secondary text-white cursor-default' : 'text-brand-dark hover:text-brand-hover'
              }`}
            >
              {item}
            </button>
          )
        )}
        <button
          type="button"
          aria-label="Next page"
          disabled={atLast}
          onClick={() => onPageChange(current)}
          className={`${NAV_BUTTON} ${atLast ? 'text-brand-disabled cursor-default' : 'text-brand-dark hover:text-brand-hover'}`}
        >
          <Chevron direction="next" />
        </button>
      </div>
      <PageSizeSelect pageSize={pageSize} options={pageSizeOptions} onChange={onPageSizeChange} />
    </nav>
  );
};

export default PipelinePagination;
